import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Tag, Search } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { blogPosts, categories } from '../mock';

const Blog = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = blogPosts.filter((post) => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-background via-background to-cyan-950/20 dark:to-cyan-950/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Blog & <span className="text-cyan-400">Insights</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
            Sharing thoughts on technology, leadership, payroll management, and digital transformation.
          </p>

          {/* Search Bar */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-background/50 backdrop-blur-sm border-cyan-500/30 focus:border-cyan-500"
            />
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Categories */}
              <Card className="border-border">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4 text-cyan-400">Categories</h3>
                  <div className="space-y-2">
                    {categories.map((category) => (
                      <Button
                        key={category.name}
                        variant={selectedCategory === category.name ? 'default' : 'ghost'}
                        className={`w-full justify-between ${
                          selectedCategory === category.name
                            ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                            : 'hover:bg-accent'
                        }`}
                        onClick={() => setSelectedCategory(category.name)}
                      >
                        <span>{category.name}</span>
                        <span className="text-xs">{category.count}</span>
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Newsletter Signup */}
              <Card className="border-border bg-gradient-to-br from-cyan-500/10 to-blue-500/10">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-2 text-cyan-400">Stay Updated</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Subscribe to get the latest insights delivered to your inbox.
                  </p>
                  <Input
                    type="email"
                    placeholder="Your email"
                    className="mb-3 bg-background/50"
                  />
                  <Button 
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                    onClick={() => alert('Newsletter signup will be implemented with backend!')}
                  >
                    Subscribe
                  </Button>
                </CardContent>
              </Card>
            </div>
          </aside>

          {/* Blog Posts */}
          <div className="lg:col-span-3">
            {filteredPosts.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-muted-foreground text-lg">No articles found.</p>
              </div>
            ) : (
              <div className="space-y-8">
                {filteredPosts.map((post) => (
                  <Card
                    key={post.id}
                    className="group border-border hover:border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/10 overflow-hidden"
                  >
                    <div className="grid md:grid-cols-5 gap-6">
                      {/* Image */}
                      <div className="md:col-span-2 relative h-64 md:h-auto overflow-hidden">
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background/20" />
                      </div>

                      {/* Content */}
                      <CardContent className="md:col-span-3 p-6 flex flex-col justify-between">
                        <div>
                          {/* Meta Info */}
                          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-3">
                            <span className="inline-flex items-center gap-1 text-cyan-400 font-medium">
                              <Tag className="h-3 w-3" />
                              {post.category}
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(post.date)}
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {post.readTime}
                            </span>
                          </div>

                          {/* Title */}
                          <h2 className="text-2xl font-bold mb-3 group-hover:text-cyan-400 transition-colors">
                            {post.title}
                          </h2>

                          {/* Excerpt */}
                          <p className="text-muted-foreground mb-4">
                            {post.excerpt}
                          </p>

                          {/* Tags */}
                          <div className="flex flex-wrap gap-2 mb-4">
                            {post.tags.map((tag, idx) => (
                              <span
                                key={idx}
                                className="text-xs px-2 py-1 rounded-full bg-cyan-500/10 text-cyan-400 border border-cyan-500/20"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Read More Button */}
                        <Button
                          variant="outline"
                          className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500 self-start"
                          onClick={() => alert('Blog post detail page will be implemented with backend!')}
                        >
                          Read Full Article
                        </Button>
                      </CardContent>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;